<template>
  <div
    :class="[
      $style.notificationItem,
      $style[`notificationItem${notificationType}`],
      (AnaliticsClasses.notification[notificationType] || ''),
      {
        awSrOnly: notificationScreenReaderOnly,
        [$style.notificationItemMarginLess]: notificationScreenReaderOnly || isMarginLess,
      },
    ]"
  >
    <div :class="$style.contentBlock">
      <lv-tooltip v-if="tooltipText && titleText" styling="primary-version2" :options="{placement: 'top-end'}">
        <template #tooltip>
          <div data-popover-item-wrapper>
            <span v-html="tooltipText" />
          </div>
        </template>
        <template #reference>
          <span :class="$style.title">
            <span v-html="titleText" />
            <lv-icon
              :name="notificationIconName"
              :size="16"
              :class="$style.notificationIcon"
            />
          </span>
        </template>
      </lv-tooltip>
      <div v-else>
        <span :class="$style.title">
          <span v-html="titleText" />
          <lv-icon
            :name="notificationIconName"
            :size="16"
            :class="$style.notificationIcon"
          />
        </span>
      </div>
      <div v-html="subtitleText" />
    </div>
    <template v-if="notificationButton">
      <aw-rounded-separator :is-horizontal="false" :class="$style.verticalSeparator" />
      <aw-rounded-separator is-horizontal :class="$style.horizontalSeparator" />
      <div :class="$style.buttonBlock">
        <component
          :is="notificationButton.is"
          v-bind="notificationButton.attrs"
        >
          <span v-html="notificationButton.html" />
        </component>
      </div>
    </template>
  </div>
</template>

<script setup>
  import { useNuxtApp } from 'nuxt/app';
  import AnaliticsClasses from '~~/common/config/AnaliticsClasses';
  import { notificationTokenizer } from '~~/common/utils/helper.js';
  import {
    LvIcon,
    LvTooltip,
  } from '~~/common/components/loginet-vue-shop/index.mjs';
  import AwRoundedSeparator from '~~/common/components/AwRoundedSeparator.vue';

  const props = defineProps({
    index: {
      type: String,
      required: true,
    },
    /**
     * @param {
     * {
     *    type: String,
     *    iconName: String,
     *    manualClose: Boolean,
     *    text: Object: {
     *      title: String,
     *      subtitle: String,
     *    }
     * }} notification
     */
    notification: {
      type: Object,
      default: () => ({ text: {} }),
    },
    close: {
      type: Function,
      default: () => {},
    },
    thinContent: {
      type: Boolean,
      default: false,
    },
    thickContent: {
      type: Boolean,
      default: false,
    },
    isMarginLess: {
      type: Boolean,
      default: false,
    },
  });

  const { $awt } = useNuxtApp();

  const notificationType = computed(() => props.notification?.type);
  const notificationIconName = computed(() => props.notification?.iconName);
  const notificationScreenReaderOnly = computed(() => props.notification?.screenReaderOnly);
  const notificationButton = computed(() => props.notification?.button);
  const notificationTexts = computed(() => {
    return notificationTokenizer(
      {
        text: props.notification?.text,
      },
      {
        awt: $awt,
      },
    );
  });
  const titleText = computed(() => notificationTexts.value.title);
  const subtitleText = computed(() => notificationTexts.value.subtitle);
  const tooltipText = computed(() => notificationTexts.value.tooltip);
</script>

<style module lang="scss" rel="stylesheet/scss">
.notificationItem {
  --notificationIconSplittedIconColor: currentColor;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  display: flex;
  flex-direction: column;
  margin: 5px 0;

  &:first-child {
    margin-top: 20px;
  }

  @include tablet(min) {
    font-size: 14px;
    line-height: 20px;
    flex-direction: row;
  }

  &MarginLess {
    margin: 0 !important;
  }

  &info {
    --notificationIconSplittedIconColor: #{$color-plum-500};
  }
}

.contentBlock,
.buttonBlock {
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  border-radius: 16px;
  background: $color-background-3;
}

.contentBlock {
  flex: 1;
  gap: 8px 0;

  @include mobile(max) {
    padding-bottom: 20px;
  }
}

.buttonBlock {
  align-items: center;
  justify-content: center;

  @include mobile(max) {
    padding-top: 20px;
  }
}

.title {
  font-weight: 900;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notificationIcon {
  color: var(--notificationIconSplittedIconColor);
}

.horizontalSeparator {
  @include tablet(min) {
    display: none !important;
  }
}

.verticalSeparator {
  @include mobile(max) {
    display: none !important;
  }
}
</style>
